import { Localized, useLocalization } from "@fluent/react"
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material"
import { useState } from "react"
import { AppId } from "../../../../app/appTypes"
import { addDocumentChangeLog, DocumentChangeLog, updateDocumentChangeLog } from "../documentsChangeLogApi"
import { useAppDispatch } from "../../../../app/hooks"
import { showError, showSuccess } from "../../../notifications/notificationsSlice"
import { SaveButton } from "../../../../app/components/SaveButton"
import { CancelButton } from "../../../../app/components/CancelButton"
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';

export interface AddEditDocumentChangeLogProps {
    documentId: AppId
    documentVersion: number
    changeLogRow: DocumentChangeLog | null
    mode: "create" | "edit"
    onSuccessfulSave: (changeLog: DocumentChangeLog) => void
    onCancelClick: () => void,
}

export const AddEditDocumentChangeLog = ({ documentId, mode, documentVersion, changeLogRow, onCancelClick, onSuccessfulSave }: AddEditDocumentChangeLogProps) => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()

    const [saving, setSaving] = useState(false)
    const [serverError, setServerError] = useState("")
    const [changeReason, setChangeReason] = useState<string>(changeLogRow?.changeReason || "");
    const [changeDescription, setChangeDescription] = useState<string>(changeLogRow?.changeDescription || "");

    const handleOkClick = async () => {
        if (!documentId || !changeReason || !changeDescription) return;

        if (mode === "edit" && !changeLogRow?.id) {
            return;
        }

        setSaving(true);
        try {
            const payload = { changeDescription, changeReason };
            let result;

            if (mode === "create") {
                result = await dispatch(addDocumentChangeLog({ documentId, payload }));

                if (addDocumentChangeLog.fulfilled.match(result)) {
                    const newChangeLog = result.payload as DocumentChangeLog;
                    dispatch(showSuccess("saved"));
                    onSuccessfulSave(newChangeLog);
                    return;
                }
            }

            if (mode === "edit") {
                result = await dispatch(updateDocumentChangeLog({ documentId, logId: changeLogRow!.id, payload }));

                if (updateDocumentChangeLog.fulfilled.match(result)) {
                    const updatedChangeLog = result.payload as DocumentChangeLog;
                    dispatch(showSuccess("saved"));
                    onSuccessfulSave(updatedChangeLog);
                    return;
                }
            }

            let errorMsg = "error";
            if (result?.payload?.kind === "http" && result.payload.problem) {
                errorMsg = result.payload.problem.title;
            }
            setServerError(errorMsg);

        } catch (error) {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };


    return <>
        <Dialog key="addEditDocumentChangeLog" open={true} maxWidth="md" fullWidth keepMounted={false}>
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: "#f9f9f9",
                    color: "text.primary",
                    fontWeight: "bold",
                }}
            >
                {mode === "create" ? (
                    <>
                        <AddCircleIcon fontSize="small" sx={{ mr: 1 }} color="success" />

                        <Localized id="document-changelog-add-entry-version" vars={{ version: documentVersion }}>
                            <span>_add entry</span>
                        </Localized>

                    </>
                ) : (
                    <>
                        <EditIcon fontSize="small" sx={{ mr: 1 }} />
                        <Localized id="document-changelog-edit-entry-version" vars={{ version: documentVersion }}>
                            <span>_edit entry</span>
                        </Localized>
                    </>
                )}
            </DialogTitle>
            <DialogContent dividers>
                <TextField
                    value={changeDescription}
                    onChange={(e) => setChangeDescription(e.target.value)}
                    fullWidth
                    multiline
                    minRows={3}
                    margin="normal"
                    variant="outlined"
                    label={l10n.getString("document-changelog-change-description", null, "_Change description")}
                />
                <TextField
                    value={changeReason}
                    onChange={(e) => setChangeReason(e.target.value)}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    label={l10n.getString("document-changelog-change-reason", null, "_Change reason")}
                />
                {serverError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {serverError}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
                <SaveButton loading={saving} onClick={handleOkClick}></SaveButton>
                <CancelButton loading={saving} onClick={onCancelClick}></CancelButton>
            </DialogActions>
        </Dialog>
    </>
}
